import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StoreState } from '~/reducer';
import { workflowConfigStateSelector } from '~/selectors';
import { WorkflowConfigInputDtoProcessType } from '~/services/Http/client.iam.generated';
import { AppDispatch } from '~/store';

import {
  addWorkflowConfig,
  getWorkflowConfigs,
  getWorkflowConfigsByUnitId,
  removeWorkflowConfig,
  updateWorkflowConfig,
} from './slice';
import {
  StoredUnitWorkflowConfigs,
  UnitWorkflowConfigs,
  WorkflowConfig,
} from './types';

const mapToUnitWorkflowConfig = (
  storedUnitWorkflows: StoredUnitWorkflowConfigs,
  workflowConfigs: WorkflowConfig[],
): UnitWorkflowConfigs => {
  const workflows =
    storedUnitWorkflows?.workflows.reduce((acc: WorkflowConfig[], uId) => {
      const found = workflowConfigs.find((wConfig) => wConfig.id === uId);
      if (!!found) {
        return [...acc, found];
      }

      return acc;
    }, []) || [];

  return {
    ...storedUnitWorkflows,
    workflows,
  };
};

export const useWorkflows = () => {
  const dispatch: AppDispatch = useDispatch();
  const { isLoading, failedToLoad, workflowConfigs, unitWorkflowConfigs } =
    useSelector((state: StoreState) => {
      return workflowConfigStateSelector(state);
    });

  const unitIdsWithWorkflows = useMemo(
    () => _.uniq(workflowConfigs.map((wConfig) => wConfig.unitId)),
    [workflowConfigs],
  );

  const getUnitWorkflowConfigs = useCallback(
    (unitId: number): Promise<UnitWorkflowConfigs> => {
      const foundUnitConfig = unitWorkflowConfigs[`${unitId}`];

      if (foundUnitConfig) {
        return Promise.resolve(
          mapToUnitWorkflowConfig(foundUnitConfig, workflowConfigs),
        );
      }

      return new Promise((resolve, reject) => {
        dispatch(getWorkflowConfigsByUnitId(unitId))
          .then((resp) => {
            resolve(
              mapToUnitWorkflowConfig(
                resp.payload as StoredUnitWorkflowConfigs,
                workflowConfigs,
              ),
            );
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    [dispatch, unitWorkflowConfigs, workflowConfigs],
  );

  const addUnitWorkflowConfig = useCallback(
    (workflowConfig: WorkflowConfig) => {
      const workflowConfigInput = {
        ...workflowConfig,
        processType:
          workflowConfig.processType as WorkflowConfigInputDtoProcessType,
      };

      return new Promise((resolve, reject) => {
        dispatch(addWorkflowConfig(workflowConfigInput))
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    [dispatch],
  );

  const updateUnitWorkflowConfig = useCallback(
    (workflowConfig: WorkflowConfig) => {
      const workflowConfigInput = {
        ...workflowConfig,
        processType:
          workflowConfig.processType as WorkflowConfigInputDtoProcessType,
      };

      return new Promise((resolve, reject) => {
        dispatch(updateWorkflowConfig(workflowConfigInput))
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    [dispatch],
  );

  const deleteUnitWorkflowConfig = useCallback(
    (workflowConfigId: string) => {
      return new Promise((resolve, reject) => {
        dispatch(removeWorkflowConfig(workflowConfigId))
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!workflowConfigs.length) {
      dispatch(getWorkflowConfigs());
    }
  }, [workflowConfigs.length, dispatch]);

  return {
    isLoading,
    failedToLoad,
    unitWorkflowConfigs,
    unitIdsWithWorkflows,
    workflowConfigs,
    getUnitWorkflowConfigs,
    addUnitWorkflowConfig,
    updateUnitWorkflowConfig,
    deleteUnitWorkflowConfig,
  };
};
